import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import React, { useEffect, useState, useMemo } from "react";
import { Pie } from "react-chartjs-2";
import "../../../Style/HomeDiagram.css";
import config from "../../../config";
import { authFetch } from "../../../utils/Security/authFetch";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
);

type StatusData = {
  status: string;
  type: string;
  count: number;
};

const HomeDiagram: React.FC = () => {
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [isAdmin, setIsAdmin] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const isDarkMode = document.body.classList.contains("dark-mode"); // or your dark mode detection

  const typeColors: Record<string, string[]> = {
    incident: ["#84ebd7", "#3abdb1"],
    change: ["#61d2c0", "#349688"],
    task: ["#3a9d88", "#1c5f50"],
    problem: ["#2b8371", "#1c5f50"],
    rma: ["#224a43", "#0d1f1a"],
  };

  const statusColors: Record<string, string[]> = isDarkMode
    ? {
        new: ["#fefefe", "#fefefe"],
        closed: ["#f7f1fc", "#f7f1fc"],
        paused: ["#eee0fb", "#eee0fb"],
        "in progress": ["#ddc1f8", "#ddc1f8"],
        reopened: ["#c5aef7", "#c5aef7"],
        reactivated: ["#b8a8f8ff", "#9980ff"],
        "in review": ["#4d1f99", "#4d1f99"],
        assigned: ["#9274ff", "#9274ff"],
        "on hold": ["#333333", "#555555"],
      }
    : {
        new: ["#c5aef7", "#c5aef7"],
        closed: ["#a791e3", "#a791e3"],
        paused: ["#7861c6", "#7861c6"],
        "in progress": ["#7861c6", "#7861c6"],
        reopened: ["#4832a4", "#4832a4"],
        reactivated: ["#7861c6", "#5a46a3"],
        "in review": ["#320881", "#320881"],
        assigned: ["#9274ff", "#9274ff"],
        "on hold": ["#000000", "#333333"],
      };
  const COLORS = [
    "#c5aef7", // light teal
    "#a791e3", // medium teal
    "#", // darker teal
    "#", // deep teal
    "#", // very dark teal
    "#1d3d38", // darker green-teal
    "#196355", // muted green-teal
    "#1e7a68", // mid teal
    "#3a9d88", // repeat lighter teal
    "#61d2c0", // soft pastel teal
  ];
  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    const isForTelebras = localStorage.getItem("isForTelebras") === "true";
    const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
    const isForNilesat = localStorage.getItem("isForNilesat") === "true";

    const userId = localStorage.getItem("userid");
    setIsAdmin(isAdmin);

   
    const isNormalUser = !isAdmin;
    const shouldSendUserId =
      (isNormalUser || isForTelebras || isForPetrobras || isForNilesat) && userId;

    const suffix = shouldSendUserId ? `?userId=${userId}` : "";

   const endpoint =
  isAdmin && (isForTelebras || isForPetrobras || isForNilesat)
    ? "/api/dashboard/telebrasstatus-counts"
    : "/api/dashboard/status-counts";

    authFetch(`${config.apiBaseUrl}${endpoint}${suffix}`)
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setStatusData(Array.isArray(data) ? data : data.data || []);
      })
      .catch(console.error);
  }, []);

  const types = isAdmin
    ? ["incident", "change", "task", "problem", "rma"]
    : ["incident", "change", "task", "rma"];

  const typeLabels: { [key: string]: string } = {
    incident: "Incident",
    change: "Changes",
    task: "Tasks",
    problem: "Problems",
    rma: "RMA",
  };

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const getChartData = (type: string) => {
    let filtered = statusData.filter((item) => item.type === type);

    // Apply status filter if selected
    if (selectedStatus) {
      filtered = filtered.filter(
        (item) => item.status.toLowerCase() === selectedStatus.toLowerCase(),
      );
    }

    const labels = filtered.map((item) => capitalize(item.status));
    const data = filtered.map((item) => item.count);

    const backgroundColor = filtered.map((item) => {
      const status = item.status.toLowerCase();
      const colors = statusColors[status] || typeColors[type];
      return colors[0];
    });

    return { labels, datasets: [{ data, backgroundColor, borderWidth: 2 }] };
  };

  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            color: getComputedStyle(document.documentElement)
              .getPropertyValue("--text-color")
              .trim(),
            font: { weight: "bold" as const },
          },
        },
        tooltip: {
          backgroundColor: getComputedStyle(document.documentElement)
            .getPropertyValue("--tooltip-bg")
            .trim(),
          titleColor: getComputedStyle(document.documentElement)
            .getPropertyValue("--tooltip-text")
            .trim(),
          bodyColor: getComputedStyle(document.documentElement)
            .getPropertyValue("--tooltip-text")
            .trim(),
          callbacks: {
            label: (ctx: any) => `${ctx.label}: ${ctx.raw}`,
          },
        },
      },
    }),
    [statusData, selectedStatus],
  );

  const now = new Date();
  const monthYear = now.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  // Unique statuses for filter buttons
  const uniqueStatuses = Array.from(
    new Set(statusData.map((item) => item.status)),
  );

  return (
    <div className="diagram">
      <h2 className="diagram-title">Status Distribution - {monthYear}</h2>

      {/* Status buttons */}
      <div className="status-filters">
        <button
          className={`status-btn ${!selectedStatus ? "active" : ""}`}
          onClick={() => setSelectedStatus(null)}
        >
          All
        </button>
        {uniqueStatuses.map((status) => (
          <button
            key={status}
            className={`status-btn ${
              selectedStatus === status ? "active" : ""
            }`}
            onClick={() => setSelectedStatus(status)}
          >
            {capitalize(status)}
          </button>
        ))}
      </div>

      <div className="card-container">
        {types.map((type) => {
          const chartData = getChartData(type);
          const hasData = chartData.datasets[0].data.some(
            (val: number) => val > 0,
          );
          return (
            <div className="card2" key={type}>
              <h3 className="card-title">{typeLabels[type]}</h3>
              {hasData ? (
                <div className="chart-wrapper">
                  <Pie data={chartData} options={options} />
                </div>
              ) : (
                <p className="no-data-text">No data available</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomeDiagram;
